import React, { useMemo, useRef } from 'react';
import Image from 'next/image';
import { useChatContext } from '@/contexts/ChatContext';
import { Loader2, Plus, MessageSquare, Search, Edit, Trash2, X } from 'lucide-react';
import { DesktopSideNavigationIcon } from '../icons';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { CheckedState } from '@radix-ui/react-checkbox';
import { cn } from '@/lib/utils';

const LONG_PRESS_DURATION_MS = 450;

interface ChatSidebarProps {
  onChatSelect?: () => void;
  onRequestClose?: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ onChatSelect, onRequestClose }) => {
  const {
    chatSessions,
    activeChatId,
    setActiveChatId,
    createNewChat,
    deleteChat,
    deleteSelectedChats,
    searchTerm,
    setSearchTerm,
    editingChatId,
    editingChatTitle,
    setEditingChatTitle,
    startEditingTitle,
    cancelEditingTitle,
    saveChatTitle,
    isLoading,
    closeSidebar,
    isSelectionMode,
    setSelectionMode,
    selectedChatIds,
    toggleChatSelection,
    selectAllChats,
    clearSelectedChats,
  } = useChatContext();
  const { t } = useTranslation();

  const filteredChatSessions = useMemo(() => {
    return chatSessions.filter(chat =>
      chat.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [chatSessions, searchTerm]);

  const selectionCount = selectedChatIds.length;
  const hasChats = filteredChatSessions.length > 0;
  const allFilteredSelected =
    hasChats &&
    filteredChatSessions.every(chat => selectedChatIds.includes(chat.id));
  const selectionStatusLabel =
    selectionCount === 0
      ? ''
      : selectionCount === 1
        ? t('chat_selected_single', '1 chat selected')
        : t('chat_selected_multiple', {
            count: selectionCount,
            defaultValue: `${selectionCount} chats selected`,
          });

  const longPressTimeoutRef = useRef<number | null>(null);

  const handleTouchStart = (chatId: string) => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current);
    }
    longPressTimeoutRef.current = window.setTimeout(() => {
      longPressTimeoutRef.current = null;
      setSelectionMode(true);
      toggleChatSelection(chatId);
    }, LONG_PRESS_DURATION_MS);
  };

  const cancelLongPress = () => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  return (
    <div
      className="flex h-full w-80 flex-col overflow-hidden rounded-xl bg-background px-4 py-4 gap-4"
      data-scroll-proxy-blocker="true"
    >
      {/* Header with Collapse Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1 px-2">
          <h2 className="text-lg text-white">{t('chats_title')}</h2>
          {chatSessions.length > 0 && selectionCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="link"
                    className="px-0 text-xs font-medium text-neutral-300 underline underline-offset-4 hover:text-white"
                  >
                    {t('select_chats', 'Select Chats')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-52 frosty-glass">
                  <DropdownMenuItem
                    disabled={selectionCount === 0}
                    onSelect={() => {
                      clearSelectedChats();
                      setSelectionMode(false);
                    }}
                  >
                    {t('clear_selection', 'Clear selection')}
                  </DropdownMenuItem>
                  <DropdownMenuCheckboxItem
                    checked={allFilteredSelected && selectionCount > 0}
                    disabled={!hasChats}
                    onCheckedChange={(checked: CheckedState) => {
                      if (checked === true) {
                        selectAllChats(filteredChatSessions.map(chat => chat.id));
                      } else {
                        clearSelectedChats();
                      }
                    }}
                  >
                    {t('select_all_chats', 'Select all')}
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={selectionCount === 0}
                    onSelect={() => {
                      if (selectionCount === 0) {
                        return;
                      }
                      const confirmed = window.confirm(
                        t('confirm_delete_selected_chats', {
                          count: selectionCount,
                          defaultValue:
                            selectionCount === 1
                              ? 'Delete the selected chat?'
                              : `Delete ${selectionCount} selected chats?`,
                        })
                      );
                      if (!confirmed) {
                        return;
                      }
                      void Promise.resolve(deleteSelectedChats()).then(() => {
                        setSelectionMode(false);
                      });
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('delete_selected_chats', 'Delete selected')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {isSelectionMode && selectionStatusLabel && (
                <span className="text-xs text-neutral-400">
                  {selectionStatusLabel}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg p-1 text-white hover:text-white hover:bg-white/10 transition-colors"
            onClick={onRequestClose ?? closeSidebar}
            title={t('common.collapse_sidebar', 'Collapse sidebar')}
          >
            <DesktopSideNavigationIcon />
          </button>
          <button
            type="button"
            className="md:hidden inline-flex h-8 w-8 items-center justify-center rounded-lg p-1 text-white hover:text-white hover:bg-white/10 transition-colors"
            onClick={onRequestClose ?? closeSidebar}
          >
            <span className="sr-only">{t('common.close_sidebar', 'Close sidebar')}</span>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div >
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
          <input
            type="text"
            placeholder={t('search_chats')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg pl-8 pr-2 py-1.5 text-sm bg-neutral-800 text-white placeholder-neutral-400 border-0 focus:outline-none focus:ring-0 focus:border-0 transition-colors"
          />
        </div>
      </div>

      {/* New Chat Button */}
      <div >
        <button
          className="flex w-full items-center justify-center rounded-lg bg-highlight/80 p-2 text-sm font-medium text-highlight-foreground transition-colors hover:bg-highlight disabled:cursor-not-allowed disabled:opacity-60"
          onClick={createNewChat}
          disabled={isLoading || isSelectionMode}
          title={t('new_chat')}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('new_chat')}
        </button>
      </div>

      {/* Chat history list */}
      <div className="flex-1 overflow-y-auto space-y-1 scrollbar-hide">
        {isLoading && chatSessions.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>  
        ) : filteredChatSessions.length === 0 ? (
            <div className="text-center text-neutral-400 text-sm mt-4">
                {searchTerm ? t('no_chats_found') : t('no_chats_yet')}
            </div>
        ) : (
          filteredChatSessions.map(chat => {
            const isActive = chat.id === activeChatId;
            const isSelected = selectedChatIds.includes(chat.id);

            return (
              <div
                key={chat.id}
                className={cn(
                  'group flex w-full items-center rounded-lg border border-transparent p-2 transition-colors',
                  isSelectionMode
                    ? isSelected
                      ? 'bg-highlight/15 text-white border-highlight/40'
                      : 'hover:bg-white/5 text-neutral-300'
                    : isActive
                      ? 'bg-white/10 text-white'
                      : 'hover:bg-white/5 hover:text-white text-neutral-300'
                )}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleChatSelection(chat.id);
                    return;
                  }
                  if (editingChatId !== chat.id) {
                    setActiveChatId(chat.id);
                    if (onChatSelect) {
                      onChatSelect();
                    }
                  }
                }}
                onTouchStart={() => handleTouchStart(chat.id)}
                onTouchEnd={cancelLongPress}
                onTouchCancel={cancelLongPress}
                title={chat.title}
              >
                {editingChatId === chat.id ? (
                  <div className="flex flex-1 items-center gap-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => {
                        if (!isSelectionMode) {
                          setSelectionMode(true);
                        }
                        toggleChatSelection(chat.id);
                      }}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                      className={cn(
                        'border-neutral-600 data-[state=checked]:border-highlight data-[state=checked]:bg-highlight',
                        !isSelectionMode && !isSelected ? 'opacity-0 group-hover:opacity-100' : ''
                      )}
                      aria-label={t('select_chat', {
                        title: chat.title,
                        defaultValue: `Select ${chat.title}`,
                      })}
                    />
                    <input
                      type="text"
                      value={editingChatTitle}
                      onChange={(e) => setEditingChatTitle(e.target.value)}
                      className="flex-1 rounded-lg border border-neutral-600 bg-neutral-800 p-1 text-sm text-white transition-colors focus:outline-none focus:ring-1 focus:ring-neutral-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveChatTitle(chat.id);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEditingTitle();
                        }
                      }}
                      onBlur={() => saveChatTitle(chat.id)}
                    />
                  </div>
                ) : (
                  <div className="flex w-full items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => {
                        if (!isSelectionMode) {
                          setSelectionMode(true);
                        }
                        toggleChatSelection(chat.id);
                      }}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                      className={cn(
                        'border-neutral-600 data-[state=checked]:border-highlight data-[state=checked]:bg-highlight',
                        !isSelectionMode && !isSelected ? 'opacity-0 group-hover:opacity-100' : ''
                      )}
                      aria-label={t('select_chat', {
                        title: chat.title,
                        defaultValue: `Select ${chat.title}`,
                      })}
                    />
                    <MessageSquare className="h-5 w-5 flex-shrink-0 text-neutral-400" />
                    <div className="flex-1 min-w-0">
                      <div className="line-clamp-1 text-sm font-medium leading-tight text-white">
                        {chat.title}
                      </div>
                    </div>
                    {!isSelectionMode && (
                      <div
                        className="ml-2 flex space-x-1 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-white/10 hover:text-white"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            startEditingTitle(chat.id, chat.title);
                          }}
                          title={t('rename_chat')}
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                        <button
                          className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-red-400/10 hover:text-red-400"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            if (window.confirm(t('confirm_delete_chat', { title: chat.title }))) {
                              deleteChat(chat.id);
                            }
                          }}
                          title={t('delete_chat')}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
 